// Imports (adicione o import do hook)
import React, { useCallback, useState } from 'react';
import { SaveProgressModal } from './SaveProgressModal';
import { usePermissions } from '../contexts/PermissionContext';
import { ChecklistGroup, ChecklistItem } from './ChecklistWindow/types';
import { Header, InstructionsPanel, Sidebar, GroupList } from './ChecklistWindow/components';
import { useChecklistState, useSaveData } from './ChecklistWindow/hooks';
import type { SaveData } from '../types/save';
import { readWithAliases, Keys } from '../lib/storage/keys';
import { sendToTerminal } from '../lib/terminalLogger';
import { STORAGE_KEYS } from '../lib/storage/constants';
import { MetadataService } from '../lib/corel/actions/metadata';
import { lockManager } from '../lib/corel/lockManager';
import { storage } from '../lib/storage/storage';
import { OPERATION_DELAYS, pipelineSleep } from '../lib/corel/pipeline/pipelineConstants';

type PreparationStep = 'lock' | 'done';

interface ChecklistWindowProps {
  onClose: () => void;
  onSave: () => void;
  contractionInfo?: {
    global: string;
    variable: boolean;
    details: { nome: string; contraction: string; }[] | null;
  };
  // Gate opcional para fluxo TEMP (local-only)
  mode?: 'default' | 'temp';
  tempPathBuilder?: (saveData: SaveData) => Promise<{ filePath: string | null; fileName: string | null }>;
}

export function ChecklistWindow({ onClose, onSave, contractionInfo: _contractionInfo, mode = 'default', tempPathBuilder }: ChecklistWindowProps) {
  const { checklists, setChecklists, totals, toggleItem, resetAll, getItem } = useChecklistState();
  const [selectedItem, setSelectedItem] = useState<ChecklistItem | null>(null);
  const [saveData, setSaveData] = useState<SaveData | undefined>(undefined);
  const { role } = usePermissions();
  const [showInstructions, setShowInstructions] = useState(false);
  const [isPreparing, setIsPreparing] = useState(true);
  const [preparationStep, setPreparationStep] = useState<PreparationStep>('lock');
  const [preparationError, setPreparationError] = useState<string | null>(null);

  // ✅ REMOVIDO: collectInitialMetadata não é mais usado aqui
  // Metadados são coletados em useSaveData.openSave() DEPOIS do unlock

  const unlockDocument = useCallback(async () => {
    try {
      const result = await lockManager.unlock(3);
      if (!result.ok) {
        sendToTerminal('⚠️ [CHECKLIST] Falha ao destravar documento ao sair', { 
          error: result.error,
          attempts: result.attempts 
        });
      }
    } catch (error) {
      sendToTerminal('⚠️ [CHECKLIST] Exceção ao destravar documento ao sair', { error: String(error) });
    }
  }, []);

  const runPreparation = useCallback(async () => {
    setIsPreparing(true);
    setPreparationError(null);
    storage.remove(Keys.export.initialMetadataSnapshot, 'session');

    try {
      // ✅ Monitor permanece ATIVO durante checklist
      // Permitindo que usuário veja mudanças em tempo real
      // Monitor será parado na Fase 4 (useSaveData) imediatamente antes do salvamento
      
      sendToTerminal('🔒 [CHECKLIST] Iniciando preparação do checklist');
      
      // Lock documento para iniciar checklist
      setPreparationStep('lock');
      const lock = await lockManager.lock('checklist', 3);
      if (!lock.ok || lock.locked !== true) {
        throw new Error(`Não foi possível travar o documento para iniciar o checklist. (${lock.attempts} tentativas)`);
      }
      await pipelineSleep(OPERATION_DELAYS.AFTER_LOCK_START, 'after_lock_start');

      // ✅ CORREÇÃO: Metadados NÃO são coletados aqui (documento está travado)
      // Metadados serão coletados DEPOIS do unlock em useSaveData.openSave()
      // Isso garante que o documento está livre quando os metadados forem coletados
      sendToTerminal('✅ [CHECKLIST] Lock ativado - metadados serão coletados após unlock');

      setPreparationStep('done');
      await pipelineSleep(OPERATION_DELAYS.AFTER_LOCK_START, 'after_lock_activation');
      setIsPreparing(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Falha ao preparar ambiente para salvamento.';
      setPreparationError(message);
      setIsPreparing(false);
      await unlockDocument();
      storage.remove(Keys.export.initialMetadataSnapshot, 'session');
      sendToTerminal('❌ [CHECKLIST] Preparação falhou', { message });
    }
  }, [unlockDocument]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      await runPreparation();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
      storage.remove(Keys.export.initialMetadataSnapshot, 'session');
      // ✅ Tenta unlock imediatamente (fire-and-forget, pois cleanup não pode ser async)
      unlockDocument();
      // ✅ FALLBACK: Agenda limpeza em 30s caso o unlock acima não complete a tempo
      // Isso garante que o documento será desbloqueado mesmo se o componente desmontar
      // antes do unlock async completar (comum no StrictMode ou fechamento rápido)
      lockManager.scheduleCleanup();
    };
  }, [runPreparation, unlockDocument]);

  const handlePreparationRetry = useCallback(() => {
    runPreparation();
  }, [runPreparation]);

  // ✅ CORREÇÃO: Removida verificação de initialMetadataSnapshot
  // Metadados são coletados DEPOIS do unlock em useSaveData.openSave()
  // A preparação está pronta quando lock foi ativado com sucesso
  const isPreparationReady = !isPreparing && !preparationError;

  // Carregar dados do storage canônico (com aliases para legado)
  React.useEffect(() => {
    try {
      const data = readWithAliases<any>(Keys.export.currentSaveData, ['currentSaveData'], { area: 'session', persistIfAliased: true });
      if (data) {
        setSaveData(data as SaveData);
        sendToTerminal('📥 [CHECKLIST] currentSaveData carregado', { hasRegras: !!data?.regras, diretorio_nome: data?.regras?.diretorio_nome || null });
      } else {
        sendToTerminal('⚠️ [CHECKLIST] currentSaveData não encontrado no session storage');
      }
    } catch (e) {
      sendToTerminal('❌ [CHECKLIST] Erro ao carregar currentSaveData do storage', {
        error: e instanceof Error ? e.message : String(e)
      });
    }
  }, []);

  // ✅ LIMPAR dados antigos com chave padronizada
  React.useEffect(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.LEGACY.CHECKLIST_PROGRESS);
      localStorage.removeItem(STORAGE_KEYS.CHECKLIST_PROGRESS); // Nova também, se existir
      // Dados antigos removidos - silencioso
    } catch (e) {
      sendToTerminal('❌ [CHECKLIST] Erro ao limpar dados antigos', {
        error: e instanceof Error ? e.message : String(e)
      });
    }
  }, []);
  // removido estado de mensagem não utilizado e duplicações de hooks/estados

  // (removido) efeito de resize e estado isMobile — não utilizado
  const handleCheck = (groupIndex: number, itemIndex: number) => {
    const item = getItem(groupIndex, itemIndex);
    if (item?.automated) return; // Não permitir alteração de itens automatizados
    toggleItem(groupIndex, itemIndex);
  };


  const handleReset = () => {
    resetAll();
    setSelectedItem(null);
    if (mode !== 'temp') {
      storage.remove(Keys.export.initialMetadataSnapshot, 'session');
      runPreparation();
    }
  };

  const toggleGroupCollapse = (groupIndex: number) => {
    setChecklists(prev => {
      const newChecklists = [...prev];
      newChecklists[groupIndex] = {
        ...newChecklists[groupIndex],
        collapsed: !newChecklists[groupIndex].collapsed
      };
      return newChecklists;
    });
  };

  const isGroupCompleted = (group: ChecklistGroup) => {
    return group.items.every(item => item.checked);
  };
  
  const getGroupProgress = (group: ChecklistGroup) => {
    const totalItems = group.items.length;
    const checkedItems = group.items.filter(item => item.checked).length;
    return (checkedItems / totalItems) * 100;
  };
  
  // getTotalProgress removido: totals.progress já provê o valor agregado

  const { showModal, openSave, onModalComplete } = useSaveData({
    saveData: saveData as SaveData | undefined,
    checklists,
    totals: { allChecked: totals.allChecked, progress: totals.progress },
    role,
    onSave,
    mode,
    tempPathBuilder,
  });

  // 🔍 DEBUG: Log showModal changes
  React.useEffect(() => {
    sendToTerminal('🎬 [CHECKLIST] showModal mudou', { 
      showModal,
      timestamp: Date.now()
    });
  }, [showModal]);

  // handleSaveComplete movido para useSaveData (onModalComplete)

  const handleInfoClick = (item: ChecklistItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedItem(item);
    setShowInstructions(true);
  };

  return (
    <div className="fixed inset-0 bg-navy-900 flex flex-col w-full h-full">
      {/* Header com título e barra de progresso geral */}
      <Header
        progress={totals.progress}
        onClose={async () => {
          await unlockDocument();
          onClose();
        }}
      />

      {/* Layout adaptativo para checklist e instruções */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Instruções em tela pequena (modo popup) */}
        {showInstructions && selectedItem && (
          <InstructionsPanel item={selectedItem} onClose={() => setShowInstructions(false)} />
        )}

        {/* Área esquerda: Grupos e Itens */}
        <div className="flex-1 p-2 sm:p-3 lg:p-4 overflow-auto">
          <GroupList
            checklists={checklists}
            isGroupCompleted={isGroupCompleted}
            getGroupProgress={getGroupProgress}
            onToggleGroup={toggleGroupCollapse}
            onCheck={handleCheck}
            onInfo={(item, e) => handleInfoClick(item, e)}
          />
        </div>

        {/* Área direita: Resumo e Ações */}
        <Sidebar
          totalProgress={totals.progress}
          isAllChecked={totals.allChecked}
          onReset={handleReset}
          onSave={async () => {
            await openSave();
          }}
          preparationReady={isPreparationReady}
        />
      </div>

      {/* Modal de progresso de salvamento */}
      {showModal && (
        <SaveProgressModal
          initialMetadataSnapshot={null}
          onComplete={() => {
            sendToTerminal('🚪 [CHECKLIST] SaveProgressModal.onComplete chamado');
            sendToTerminal('📋 [DATA]', { 
              willCallOnModalComplete: true,
              willCallOnSave: true,
              willCallOnClose: false 
            });
            onModalComplete();
          }}
        />
      )}

      {(isPreparing || preparationError) && (
        <ChecklistPreparationOverlay
          step={preparationStep}
          isRunning={isPreparing}
          error={preparationError}
          onRetry={handlePreparationRetry}
        />
      )}
    </div>
  );
}

interface ChecklistPreparationOverlayProps {
  step: PreparationStep;
  isRunning: boolean;
  error: string | null;
  onRetry: () => void;
}

const PREPARATION_STEPS: Array<{ id: PreparationStep; label: string }> = [
  { id: 'lock', label: 'Travando documento no CorelDRAW' },
];

const PREPARATION_ORDER: PreparationStep[] = ['lock', 'done'];

function ChecklistPreparationOverlay({ step, isRunning, error, onRetry }: ChecklistPreparationOverlayProps) {
  const currentIndex = step === 'done' ? PREPARATION_ORDER.length - 1 : PREPARATION_ORDER.indexOf(step);

  const getStatusForStep = (stepId: PreparationStep) => {
    if (error && step === stepId) return 'error';
    const stepIndex = PREPARATION_ORDER.indexOf(stepId);
    if (step === 'done') return 'done';
    if (currentIndex > stepIndex) return 'done';
    if (currentIndex === stepIndex) return 'active';
    return 'pending';
  };

  const renderStatusIndicator = (status: string) => {
    if (status === 'done') {
      return <div className="w-3 h-3 rounded-full bg-emerald-400" />;
    }
    if (status === 'active') {
      return <div className="w-3 h-3 rounded-full border-2 border-yellow-300 border-t-transparent animate-spin" />;
    }
    if (status === 'error') {
      return <div className="w-3 h-3 rounded-full bg-red-400 animate-pulse" />;
    }
    return <div className="w-3 h-3 rounded-full bg-gray-500/70" />;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm px-4">
      <div className="bg-navy-800 border border-navy-700 rounded-xl shadow-2xl w-full max-w-md p-5">
        <h2 className="text-lg font-semibold text-white mb-1">Preparando ambiente para salvamento</h2>
        <p className="text-xs text-gray-400 mb-4">
          Aguarde enquanto travamos o documento e coletamos o snapshot inicial.
        </p>

        <ul className="space-y-3">
          {PREPARATION_STEPS.map(({ id, label }) => {
            const status = getStatusForStep(id);
            return (
              <li key={id} className="flex items-start gap-3">
                {renderStatusIndicator(status)}
                <div>
                  <div className="text-sm font-medium text-white">{label}</div>
                  {status === 'active' && (
                    <div className="text-xs text-yellow-200/90">Executando…</div>
                  )}
                  {status === 'error' && error && (
                    <div className="text-xs text-red-200/90">{error}</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {error ? (
          <div className="mt-4 bg-red-500/10 border border-red-500/40 text-red-200 text-sm rounded-lg p-4">
            <p className="mb-3 font-medium">Não foi possível concluir a preparação.</p>
            <button
              type="button"
              onClick={onRetry}
              className="w-full px-3 py-2 text-xs font-semibold bg-red-400 text-navy-900 rounded hover:bg-red-300 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <div className="mt-4 text-xs text-gray-400">
            {isRunning ? 'Este processo leva apenas alguns instantes.' : 'Documento pronto para checklist.'}
          </div>
        )}
      </div>
    </div>
  );
}
